import { useEffect, useRef, memo } from "react";
import { createRoot } from "react-dom/client";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

/**
 * Construye el JSX para un markdown determinado, con manejo especial
 * de enlaces de búsqueda que deben navegar al buscador del padre.
 */
export function buildMarkdownJSX(markdown, parentBase) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSanitize]}
      components={{
        a: ({ href, children, ...props }) => {
          const safeHref = href || "";
          const isSearchLink = safeHref.includes("?q=") || safeHref.includes("&q=");
          const handleClick = (e) => {
            if (!isSearchLink) return;
            e.preventDefault();
            e.stopPropagation();
            const base = parentBase ? parentBase.replace(/\/+$/, "") : "";
            const query = safeHref.startsWith("?") ? safeHref : "?" + safeHref.split("?").pop();
            const fullUrl = base + "/buscador/" + query;
            try {
              if (window.top !== window.self) window.top.location.href = fullUrl;
              else window.location.href = fullUrl;
            } catch {
              window.parent.postMessage({ type: "navigate", url: fullUrl }, base || "*");
            }
          };
          return (
            <a href={safeHref} target="_top" rel="noreferrer" onClick={handleClick} {...props}>
              {children}
            </a>
          );
        },
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
}

/**
 * Renderizado markdown imperativo para streaming.
 * Expone __updateMarkdown(md) en el DOM ref para reemplazar el contenido
 * sin re-render de React (mejora performance durante el typing).
 */
export const StreamingMarkdown = memo(({ targetRef, parentBase }) => {
  const rootRef = useRef(null);

  useEffect(() => {
    if (targetRef.current && !rootRef.current) {
      rootRef.current = createRoot(targetRef.current);
    }
  }, [targetRef]);

  useEffect(() => {
    if (!targetRef.current) return;
    targetRef.current.__updateMarkdown = (md) => {
      if (rootRef.current) {
        rootRef.current.render(buildMarkdownJSX(md, parentBase));
      }
    };
    return () => {
      if (targetRef.current) {
        delete targetRef.current.__updateMarkdown;
      }
    };
  }, [targetRef, parentBase]);

  return <div className="summary-text" ref={targetRef} />;
});
StreamingMarkdown.displayName = "StreamingMarkdown";

/**
 * Markdown estático (sin streaming).
 */
export const TextContent = memo(
  ({ markdown, parentBase }) => (
    <div className="summary-text">
      {buildMarkdownJSX(markdown, parentBase)}
    </div>
  ),
  (prev, next) => prev.markdown === next.markdown && prev.parentBase === next.parentBase
);
TextContent.displayName = "TextContent";